import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Users, 
  UserPlus, 
  User,
  ArrowRight,
  CheckCircle,
  Upload,
  FileSpreadsheet
} from 'lucide-react';
import { FileUpload } from './FileUpload';
import { FieldMapping } from './FieldMapping';
import { FamilyAssignment } from './FamilyAssignment';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

type Step = 'intro' | 'families' | 'parents' | 'students' | 'review' | 'complete';
type SubStep = 'upload' | 'mapping' | 'assign' | 'done';

interface StepData {
  file?: File;
  headers?: string[];
  sampleData?: string[][];
  mappings?: Record<string, string>;
  assignments?: Record<string, string>; // recordId -> familyId
  workerResults?: any;
}

interface FamilyRecord {
  id: string;
  name: string;
  tempId?: string; // For families created in this session
}

export function SequentialFamilyImport() {
  const [currentStep, setCurrentStep] = useState<Step>('intro');
  const [currentSubStep, setCurrentSubStep] = useState<SubStep>('upload');
  const [familiesData, setFamiliesData] = useState<StepData>({});
  const [parentsData, setParentsData] = useState<StepData>({});
  const [studentsData, setStudentsData] = useState<StepData>({});
  const [availableFamilies, setAvailableFamilies] = useState<FamilyRecord[]>([]);
  const [createdFamilies, setCreatedFamilies] = useState<FamilyRecord[]>([]);
  const { toast } = useToast();
  const { organizationId } = useAuth();

  // Load existing families on component mount
  React.useEffect(() => {
    loadExistingFamilies();
  }, []);

  const loadExistingFamilies = async () => {
    const { data, error } = await supabase
      .from('families')
      .select('id, name')
      .order('name');
    
    if (!error && data) {
      setAvailableFamilies(data);
    }
  };

  const steps = [
    { id: 'intro', label: 'Introduction', icon: Users },
    { id: 'families', label: 'Import Families', icon: Users, optional: true },
    { id: 'parents', label: 'Import Parents', icon: UserPlus, optional: true },
    { id: 'students', label: 'Import Students', icon: User },
    { id: 'review', label: 'Review & Import', icon: CheckCircle }
  ];

  const getCurrentStepIndex = () => steps.findIndex(s => s.id === currentStep);
  const progressPercentage = ((getCurrentStepIndex() + 1) / steps.length) * 100;

  const handleFileUploaded = (step: 'families' | 'parents' | 'students') => (
    file: File, 
    analysis: any
  ) => {
    const stepData = { 
      file, 
      headers: analysis.headers,
      sampleData: analysis.previewData?.map((row: any) => 
        analysis.headers.map((header: string) => row.original[header] || '')
      ) || [],
      workerResults: analysis
    };
    
    if (step === 'families') setFamiliesData(stepData);
    else if (step === 'parents') setParentsData(stepData);
    else if (step === 'students') setStudentsData(stepData);
    
    setCurrentSubStep('mapping');
  };

  const handleMappingComplete = (step: 'families' | 'parents' | 'students') => (
    mappings: Record<string, string>
  ) => {
    if (step === 'families') {
      setFamiliesData(prev => ({ ...prev, mappings }));
      // Skip assignment for families, go to next step
      handleNextStep();
    } else {
      // For parents/students, go to assignment step
      if (step === 'parents') setParentsData(prev => ({ ...prev, mappings }));
      else setStudentsData(prev => ({ ...prev, mappings }));
      setCurrentSubStep('assign');
    }
  };

  const handleNextStep = () => {
    const stepOrder: Step[] = ['intro', 'families', 'parents', 'students', 'review', 'complete'];
    const currentIndex = stepOrder.indexOf(currentStep);
    
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
      setCurrentSubStep('upload');
    }
  };

  const handleSkipStep = () => {
    handleNextStep();
  };

  const renderIntroStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Welcome to Sequential Family Import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-gray-600">
          This guided process helps you import complete family data while maintaining all relationships.
        </p>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Users className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold">Step 1: Families (Optional)</h3>
              <p className="text-sm text-gray-600">Import family records with billing preferences</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <UserPlus className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold">Step 2: Parents (Optional)</h3>
              <p className="text-sm text-gray-600">Import parent contacts and assign them to families</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <User className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold">Step 3: Students (Required)</h3>
              <p className="text-sm text-gray-600">Import students and assign them to families</p>
            </div>
          </div>
        </div>

        <Alert>
          <AlertDescription>
            The AI will help map your fields and suggest family relationships based on names and other data.
          </AlertDescription>
        </Alert>

        <div className="flex justify-end">
          <Button onClick={handleNextStep} size="lg">
            Get Started
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderFamiliesStep = () => {
    if (currentSubStep === 'upload') {
      return (
        <Card>
          <CardHeader>
            <CardTitle>Import Families (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUpload
              importType="families"
              organizationId={organizationId || ''}
              onFileUploaded={handleFileUploaded('families')}
              onBack={() => setCurrentStep('intro')}
            />
            <div className="mt-6 flex justify-between">
              <Button variant="outline" onClick={handleSkipStep}>
                Skip This Step
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (currentSubStep === 'mapping' && familiesData.headers) {
      return (
        <FieldMapping
          importType="families"
          fileData={{
            headers: familiesData.headers,
            sampleData: familiesData.sampleData || [],
            totalRows: familiesData.sampleData?.length || 0
          }}
          suggestedMappings={{}}
          workerResults={familiesData.workerResults}
          onMappingComplete={handleMappingComplete('families')}
          onBack={() => setCurrentSubStep('upload')}
        />
      );
    }
  };

  const renderParentsStep = () => {
    // Similar structure to families but with assignment UI
    if (currentSubStep === 'upload') {
      return (
        <Card>
          <CardHeader>
            <CardTitle>Import Parents (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUpload
              importType="families"
              organizationId={organizationId || ''}
              onFileUploaded={handleFileUploaded('parents')}
              onBack={() => setCurrentStep('families')}
            />
            <div className="mt-6 flex justify-between">
              <Button variant="outline" onClick={handleSkipStep}>
                Skip This Step
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (currentSubStep === 'mapping' && parentsData.headers) {
      return (
        <FieldMapping
          importType="families" // Will need to add 'parents' type
          fileData={{
            headers: parentsData.headers,
            sampleData: parentsData.sampleData || [],
            totalRows: parentsData.sampleData?.length || 0
          }}
          suggestedMappings={{}}
          workerResults={parentsData.workerResults}
          onMappingComplete={handleMappingComplete('parents')}
          onBack={() => setCurrentSubStep('upload')}
        />
      );
    }

    if (currentSubStep === 'assign' && parentsData.sampleData) {
      // Transform sample data into records for assignment
      const parentRecords = parentsData.sampleData.map((row, index) => {
        const recordData: Record<string, any> = {};
        parentsData.headers?.forEach((header, headerIndex) => {
          recordData[header] = row[headerIndex];
        });
        return {
          id: `parent-${index}`,
          data: recordData
        };
      });

      return (
        <FamilyAssignment
          type="parents"
          records={parentRecords}
          availableFamilies={[...availableFamilies, ...createdFamilies]}
          mappings={parentsData.mappings || {}}
          onAssignmentsComplete={(assignments) => {
            setParentsData(prev => ({ ...prev, assignments }));
            handleNextStep();
          }}
          onBack={() => setCurrentSubStep('mapping')}
        />
      );
    }
  };

  const renderStudentsStep = () => {
    if (currentSubStep === 'upload') {
      return (
        <Card>
          <CardHeader>
            <CardTitle>Import Students (Required)</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUpload
              importType="students"
              organizationId={organizationId || ''}
              onFileUploaded={handleFileUploaded('students')}
              onBack={() => setCurrentStep('parents')}
            />
          </CardContent>
        </Card>
      );
    }

    if (currentSubStep === 'mapping' && studentsData.headers) {
      return (
        <FieldMapping
          importType="students"
          fileData={{
            headers: studentsData.headers,
            sampleData: studentsData.sampleData || [],
            totalRows: studentsData.sampleData?.length || 0
          }}
          suggestedMappings={{}}
          workerResults={studentsData.workerResults}
          onMappingComplete={handleMappingComplete('students')}
          onBack={() => setCurrentSubStep('upload')}
        />
      );
    }

    if (currentSubStep === 'assign' && studentsData.sampleData) {
      // Transform sample data into records for assignment
      const studentRecords = studentsData.sampleData.map((row, index) => {
        const recordData: Record<string, any> = {};
        studentsData.headers?.forEach((header, headerIndex) => {
          recordData[header] = row[headerIndex];
        });
        return {
          id: `student-${index}`,
          data: recordData
        };
      });

      return (
        <FamilyAssignment
          type="students"
          records={studentRecords}
          availableFamilies={[...availableFamilies, ...createdFamilies]}
          mappings={studentsData.mappings || {}}
          onAssignmentsComplete={(assignments) => {
            setStudentsData(prev => ({ ...prev, assignments }));
            handleNextStep();
          }}
          onBack={() => setCurrentSubStep('mapping')}
        />
      );
    }

    return null;
  };

  const renderReviewStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Review Your Import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {familiesData.file && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileSpreadsheet className="w-5 h-5 text-gray-600" />
                <span>Families: {familiesData.file.name}</span>
              </div>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          )}
          
          {parentsData.file && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileSpreadsheet className="w-5 h-5 text-gray-600" />
                <span>Parents: {parentsData.file.name}</span>
              </div>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          )}
          
          {studentsData.file && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <FileSpreadsheet className="w-5 h-5 text-gray-600" />
                <span>Students: {studentsData.file.name}</span>
              </div>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          )}
        </div>

        <Alert>
          <AlertDescription>
            Ready to import your data. This will create all records and maintain proper relationships.
          </AlertDescription>
        </Alert>

        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={() => setCurrentStep('intro')}>
            Start Over
          </Button>
          <Button onClick={() => {
            toast({
              title: "Import Started",
              description: "Processing your files...",
            });
            // TODO: Implement actual import
            setCurrentStep('complete');
          }}>
            Start Import
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderCompleteStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Import Complete!</CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-6">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
        <p className="text-lg">Your family data has been successfully imported.</p>
        <Button onClick={() => window.location.href = '/admin/families'}>
          View Families
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`flex items-center space-x-1 ${
                getCurrentStepIndex() >= index ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <step.icon className="w-4 h-4" />
              <span>{step.label}</span>
              {step.optional && <span className="text-xs">(Optional)</span>}
            </div>
          ))}
        </div>
        <Progress value={progressPercentage} className="h-2" />
      </div>

      {/* Current Step Content */}
      {currentStep === 'intro' && renderIntroStep()}
      {currentStep === 'families' && renderFamiliesStep()}
      {currentStep === 'parents' && renderParentsStep()}
      {currentStep === 'students' && renderStudentsStep()}
      {currentStep === 'review' && renderReviewStep()}
      {currentStep === 'complete' && renderCompleteStep()}
    </div>
  );
}